// src/components/work_orders/WorkOrderChat.js
// Enhanced chat component with advanced debugging, global WebSocket sync, and modern UI features
// including Voice Recording, Image Lightbox, Date Grouping, Realtime Sync Hardening,
// Optimistic Message Rendering, Typing Indicators, Read Receipts, and Reconnect Recovery.

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box, VStack, Text, Textarea, Button, Icon, Spinner, Flex,
  useColorModeValue, useToast, HStack, IconButton,
  Alert, AlertIcon, AlertDescription, AlertTitle, Tag, Badge,
  Tooltip, Divider, Stack, Image, Skeleton, Modal, ModalOverlay,
  ModalContent, ModalBody, ModalCloseButton, useDisclosure, Portal
} from '@chakra-ui/react';
import {
  MdSend, MdCloudOff, MdCloudQueue, MdCloudDone, MdErrorOutline,
  MdRefresh, MdBugReport, MdAttachFile, MdMic, MdStop, MdImage,
  MdClose, MdDownload, MdZoomIn, MdSchedule, MdCheck, MdDoneAll,
  MdKeyboardArrowDown, MdWarningAmber
} from 'react-icons/md';

import { config } from '../../config';
import { getAuthToken, getUserInfo } from '../../utils/auth';
import { formatTimeAgo } from '../../utils/date';
import webSocketService from '../../services/websocketService';

// ============================================================================
// DEBUGGING CONFIGURATION
// ============================================================================

const DEBUG_CONFIG = {
  enabled: process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEBUG === 'true',
  logLevel: 'all',
  logToConsole: true,
  logToUI: false,
  logWebSocket: true,
  logPerformance: true,
  maxConsoleMessages: 200,
};

// ============================================================================
// REALTIME CONFIGURATION (ADDED)
// ============================================================================

/**
 * Tunables for the realtime layer. Kept separate from DEBUG_CONFIG so the
 * behaviour of the sync engine is independent of whether debugging is on.
 */
const REALTIME_CONFIG = {
  // Heartbeat we send to the server so it knows the room membership is alive.
  heartbeatIntervalMs: 25000,
  // If we go this long without any inbound frame while "connected", we assume
  // the socket is a zombie and force a reconciliation fetch.
  stalenessThresholdMs: 60000,
  // Polling fallback ONLY engages when the socket is not connected. When the
  // socket is healthy this timer performs no network calls at all.
  fallbackPollIntervalMs: 15000,
  // After a reconnect we always reconcile against the REST endpoint because
  // frames emitted while we were offline are not replayed by the pub/sub layer.
  reconcileOnReconnect: true,
  // Number of subscribe retries when the socket reports connected but the
  // server has not acknowledged our room join.
  maxSubscribeRetries: 5,
  subscribeRetryDelayMs: 1200,
  // How long an optimistic bubble is allowed to stay pending before we flag it.
  optimisticTimeoutMs: 20000,
  // Typing indicator lifetime before we auto-expire a stale "user is typing".
  typingExpiryMs: 4000,
  // Debounce for outbound typing frames.
  typingEmitThrottleMs: 1800,
  // Scroll tolerance (px) for deciding whether the user is pinned to bottom.
  autoScrollThresholdPx: 120,
};

/**
 * Every WebSocket event name this component cares about. Backends differ in
 * naming between deployments, so we subscribe to all plausible aliases and
 * normalise them into one internal handler. This is additive: unknown events
 * are simply never fired.
 */
const WS_EVENT_ALIASES = {
  newMessage: [
    'new_work_order_message',
    'work_order_message',
    'workOrderMessage',
    'message_created',
    'chat_message',
  ],
  messageUpdated: [
    'work_order_message_updated',
    'message_updated',
  ],
  messageDeleted: [
    'work_order_message_deleted',
    'message_deleted',
  ],
  typing: [
    'work_order_typing',
    'typing',
  ],
  read: [
    'work_order_message_read',
    'message_read',
  ],
  subscribed: [
    'work_order_subscribed',
    'subscribed',
  ],
};

class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
  }
  start(operation) {
    this.metrics.set(operation, {
      startTime: performance.now(),
      startMemory: performance.memory?.usedJSHeapSize || 0
    });
  }
  end(operation) {
    const metric = this.metrics.get(operation);
    if (!metric) return null;
    const duration = performance.now() - metric.startTime;
    const memoryDelta = (performance.memory?.usedJSHeapSize || 0) - metric.startMemory;
    this.metrics.delete(operation);
    return {
      duration: duration.toFixed(2),
      memory: (memoryDelta / 1024 / 1024).toFixed(2)
    };
  }
  getStats() {
    return {
      activeOperations: this.metrics.size,
      operations: Array.from(this.metrics.keys())
    };
  }
}

const perfMonitor = new PerformanceMonitor();

const debugLog = {
  _messageCount: 0,
  _log(level, component, message, data = null, color = '#000') {
    if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logToConsole) return;
    if (this._messageCount >= DEBUG_CONFIG.maxConsoleMessages) return;
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] [${component}] ${message}`;
    if (data) {
      console[level](`%c${logMessage}`, `color: ${color}; font-weight: bold;`, data);
    } else {
      console[level](`%c${logMessage}`, `color: ${color}; font-weight: bold;`);
    }
    this._messageCount++;
  },
  info: (component, message, data = null) => debugLog._log('log', component, `ℹ️ ${message}`, data, '#3182CE'),
  warn: (component, message, data = null) => debugLog._log('warn', component, `⚠️ ${message}`, data, '#D69E2E'),
  error: (component, message, error = null) => {
    debugLog._log('error', component, `❌ ${message}`, error, '#E53E3E');
    if (error?.stack) console.error('Stack:', error.stack);
  },
  websocket: (component, event, data = null) => {
    if (DEBUG_CONFIG.logWebSocket) debugLog._log('log', component, `🔌 WebSocket: ${event}`, data, '#805AD5');
  },
  performance: (component, operation, metrics) => {
    if (DEBUG_CONFIG.logPerformance) debugLog._log('log', component, `⚡ ${operation}`, metrics, '#38B2AC');
  },
  render: (component, phase, data = null) => {
    debugLog._log('log', component, `🎨 ${phase}`, data, '#48BB78');
  }
};

// ============================================================================
// API & UTILITY CONFIGURATION
// ============================================================================

const API_BASE_URL = config.apiUrl;

const getMediaUrl = (mediaPath) => {
  if (!mediaPath) return '';
  if (mediaPath.startsWith('http')) return mediaPath;
  const baseUrl = API_BASE_URL.replace(/\/$/, '');
  const safePath = mediaPath.startsWith('/') ? mediaPath : `/${mediaPath}`;
  return `${baseUrl}${safePath}`;
};

const handleApiError = async (response, resourceName = 'resource') => {
  let errorMsg = `HTTP error ${response.status} fetching ${resourceName}: ${response.statusText}`;
  if (response.headers.get('content-type')?.includes('application/json')) {
    try {
      const errData = await response.json();
      errorMsg = errData.message || (errData.errors ? errData.errors.map(e => e.msg).join(', ') : errorMsg);
    } catch (parseErr) {
      debugLog.warn('API', 'Failed to parse error response', parseErr);
    }
  }
  throw new Error(errorMsg);
};

const fetchMessages = async (workOrderId, token) => {
  if (!workOrderId) return [];
  perfMonitor.start('fetchMessages');
  const response = await fetch(`${API_BASE_URL}/workorders/${workOrderId}/messages`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) await handleApiError(response, 'messages');
  const data = await response.json();
  perfMonitor.end('fetchMessages');
  return Array.isArray(data) ? data : [];
};

const postMessageApi = async (workOrderId, messageText, token) => {
  if (!workOrderId || !messageText) throw new Error('Missing parameters');
  perfMonitor.start('postMessage');
  const response = await fetch(`${API_BASE_URL}/workorders/${workOrderId}/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ message_text: messageText })
  });
  if (!response.ok) await handleApiError(response, 'posting message');
  perfMonitor.end('postMessage');
  return await response.json();
};

const postMediaApi = async (workOrderId, file, messageText, duration, token) => {
  if (!workOrderId || !file) throw new Error('Missing file or work order ID');
  const formData = new FormData();
  formData.append('mediaFile', file);
  if (messageText) formData.append('message_text', messageText);
  if (duration) formData.append('media_duration_seconds', duration);

  const response = await fetch(`${API_BASE_URL}/workorders/${workOrderId}/messages/upload`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData
  });

  if (!response.ok) await handleApiError(response, 'uploading media');
  return await response.json();
};

// ============================================================================
// REALTIME UTILITIES (ADDED)
// ============================================================================

/**
 * Normalises any id-ish value to a comparable string.
 *
 * THE CRITICAL BUG THIS SOLVES:
 * The original code compared `msg.work_order_id === workOrderId` with strict
 * equality. The REST/WS payloads emit `work_order_id` as a NUMBER (MySQL INT),
 * while the prop arrives as a STRING (from useParams / PropTypes.string).
 * `123 === "123"` is false, so EVERY inbound realtime frame was discarded and
 * the user had to hit refresh. Coercing both sides fixes realtime delivery.
 */
const normaliseId = (value) => {
  if (value === null || value === undefined) return null;
  return String(value).trim();
};

/**
 * Returns true when two id-ish values refer to the same entity regardless of
 * whether the transport delivered them as a number or a string.
 */
const idsMatch = (a, b) => {
  const na = normaliseId(a);
  const nb = normaliseId(b);
  if (na === null || nb === null) return false;
  return na === nb;
};

/**
 * Different backends nest the message differently depending on which emitter
 * produced the frame (direct WS send vs Redis pub/sub adapter vs socket.io
 * compatibility shim). This unwraps all known shapes without assuming any.
 */
const extractMessagePayload = (data) => {
  if (!data) return null;
  if (data.payload && typeof data.payload === 'object') {
    // Some adapters double-wrap: { payload: { payload: {...} } }
    if (data.payload.payload && typeof data.payload.payload === 'object') {
      return data.payload.payload;
    }
    if (data.payload.message && typeof data.payload.message === 'object') {
      return data.payload.message;
    }
    return data.payload;
  }
  if (data.message && typeof data.message === 'object') return data.message;
  if (data.data && typeof data.data === 'object') return data.data;
  // Bare frame — the message itself was emitted at the top level.
  if (data.id || data.message_text || data.media_url) return data;
  return null;
};

/**
 * Reads the work order id off a message regardless of casing convention.
 */
const readWorkOrderId = (msg) => {
  if (!msg) return null;
  return (
    msg.work_order_id ??
    msg.workOrderId ??
    msg.workorder_id ??
    msg.wo_id ??
    null
  );
};

/**
 * Stable sort comparator. Falls back to id ordering when timestamps collide,
 * which happens frequently with bulk inserts sharing the same second.
 */
const compareMessages = (a, b) => {
  const ta = new Date(a.sent_at || a.created_at || 0).getTime();
  const tb = new Date(b.sent_at || b.created_at || 0).getTime();
  if (ta !== tb) return ta - tb;
  const ia = Number(a.id) || 0;
  const ib = Number(b.id) || 0;
  return ia - ib;
};

/**
 * Merges an inbound message into an existing list without producing
 * duplicates. Handles three cases:
 *   1. The message id already exists  -> replace in place (server wins).
 *   2. An optimistic placeholder matches -> upgrade the placeholder.
 *   3. Genuinely new                  -> insert and re-sort.
 */
const mergeMessage = (existing, incoming) => {
  if (!incoming) return existing;

  const incomingId = normaliseId(incoming.id);

  // Case 1: exact id already present.
  if (incomingId) {
    const idx = existing.findIndex(m => normaliseId(m.id) === incomingId);
    if (idx !== -1) {
      const next = [...existing];
      next[idx] = { ...next[idx], ...incoming, __pending: false, __failed: false };
      return next;
    }
  }

  // Case 2: reconcile an optimistic bubble we created locally.
  const optimisticIdx = existing.findIndex(m => {
    if (!m.__optimistic || m.__failed) return false;
    if (m.__clientId && incoming.client_id && m.__clientId === incoming.client_id) {
      return true;
    }
    // Fallback heuristic: same sender, same text, sent within 60s.
    if (!idsMatch(m.sender_user_id, incoming.sender_user_id)) return false;
    const sameText = (m.message_text || '') === (incoming.message_text || '');
    const bothHaveMedia = Boolean(m.__localFileName) === Boolean(incoming.media_url);
    if (!sameText && !bothHaveMedia) return false;
    const delta = Math.abs(
      new Date(incoming.sent_at || Date.now()).getTime() -
      new Date(m.sent_at || Date.now()).getTime()
    );
    return delta < 60000;
  });

  if (optimisticIdx !== -1) {
    const next = [...existing];
    next[optimisticIdx] = {
      ...incoming,
      __optimistic: false,
      __pending: false,
      __failed: false,
    };
    next.sort(compareMessages);
    return next;
  }

  // Case 3: brand new message.
  const next = [...existing, incoming];
  next.sort(compareMessages);
  return next;
};

/**
 * Reconciles a freshly fetched server list with any locally pending optimistic
 * bubbles so a background refetch never wipes a message the user just sent.
 */
const reconcileServerList = (serverList, currentList) => {
  const safeServer = Array.isArray(serverList) ? [...serverList] : [];
  safeServer.sort(compareMessages);

  const stillPending = currentList.filter(m => {
    if (!m.__optimistic) return false;
    if (m.__failed) return true;
    // Drop the optimistic copy if the server list already contains it.
    const matched = safeServer.some(s => {
      if (m.__clientId && s.client_id && m.__clientId === s.client_id) return true;
      if (!idsMatch(s.sender_user_id, m.sender_user_id)) return false;
      const sameText = (s.message_text || '') === (m.message_text || '');
      const delta = Math.abs(
        new Date(s.sent_at || 0).getTime() - new Date(m.sent_at || 0).getTime()
      );
      return sameText && delta < 120000;
    });
    return !matched;
  });

  const merged = [...safeServer, ...stillPending];
  merged.sort(compareMessages);
  return merged;
};

/**
 * Generates a collision-resistant client id for optimistic messages so the
 * server echo can be matched back precisely.
 */
const createClientId = () => {
  const rand = Math.random().toString(36).slice(2, 10);
  return `cid_${Date.now()}_${rand}`;
};

/**
 * Groups messages by date for chronological dividers
 */
const groupMessagesByDate = (messages) => {
  const groups = {};
  messages.forEach(msg => {
    const date = new Date(msg.sent_at || Date.now());
    const dateString = date.toLocaleDateString(undefined, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
    if (!groups[dateString]) groups[dateString] = [];
    groups[dateString].push(msg);
  });
  return groups;
};

// ============================================================================
// SUBCOMPONENTS
// ============================================================================

/**
 * Image Lightbox Modal for viewing full-size attachments
 */
const ImageLightbox = ({ isOpen, onClose, src }) => (
  <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
    <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(10px)" />
    <ModalContent bg="transparent" boxShadow="none">
      <ModalCloseButton color="white" size="lg" top={4} right={4} />
      <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
        <Image
          src={src}
          maxH="90vh"
          maxW="100%"
          objectFit="contain"
          borderRadius="md"
          fallback={<Spinner color="white" size="xl" />}
        />
      </ModalBody>
    </ModalContent>
  </Modal>
);

ImageLightbox.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  src: PropTypes.string
};

/**
 * Small delivery-state glyph rendered inside the user's own bubbles.
 * pending  -> clock
 * sent     -> single check
 * read     -> double check
 * failed   -> warning triangle
 */
const DeliveryState = ({ state, color }) => {
  const map = {
    pending: { icon: MdSchedule, label: 'Sending…' },
    sent: { icon: MdCheck, label: 'Sent' },
    read: { icon: MdDoneAll, label: 'Read' },
    failed: { icon: MdWarningAmber, label: 'Failed to send' },
  };
  const entry = map[state] || map.sent;
  return (
    <Tooltip label={entry.label} placement="left">
      <span>
        <Icon as={entry.icon} boxSize="12px" color={color} ml={1} verticalAlign="middle" />
      </span>
    </Tooltip>
  );
};

DeliveryState.propTypes = {
  state: PropTypes.string,
  color: PropTypes.string
};

/**
 * Animated three-dot typing indicator shown when peers are composing.
 */
const TypingIndicator = ({ names, bg, borderColor, textColor }) => {
  if (!names || names.length === 0) return null;
  const label = names.length === 1
    ? `${names[0]} is typing`
    : names.length === 2
      ? `${names[0]} and ${names[1]} are typing`
      : `${names.length} people are typing`;

  return (
    <Flex w="100%" justify="flex-start" mt={1}>
      <Flex
        align="center"
        bg={bg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        borderBottomLeftRadius="sm"
        px={3}
        py={2}
        boxShadow="sm"
      >
        <Text fontSize="xs" color={textColor} mr={2} fontWeight="500">
          {label}
        </Text>
        <HStack spacing="3px">
          <Box className="typing-dot typing-dot-1" w="5px" h="5px" borderRadius="full" bg={textColor} />
          <Box className="typing-dot typing-dot-2" w="5px" h="5px" borderRadius="full" bg={textColor} />
          <Box className="typing-dot typing-dot-3" w="5px" h="5px" borderRadius="full" bg={textColor} />
        </HStack>
      </Flex>
    </Flex>
  );
};

TypingIndicator.propTypes = {
  names: PropTypes.array,
  bg: PropTypes.string,
  borderColor: PropTypes.string,
  textColor: PropTypes.string
};

/**
 * Floating "jump to latest" pill that appears when new messages arrive while
 * the user has scrolled up reading history.
 */
const NewMessagePill = ({ count, onClick }) => {
  if (count <= 0) return null;
  return (
    <Flex
      position="absolute"
      bottom="90px"
      left="50%"
      transform="translateX(-50%)"
      zIndex={5}
      justify="center"
    >
      <Button
        size="sm"
        colorScheme="blue"
        borderRadius="full"
        boxShadow="lg"
        leftIcon={<Icon as={MdKeyboardArrowDown} boxSize={5} />}
        onClick={onClick}
      >
        {count} new {count === 1 ? 'message' : 'messages'}
      </Button>
    </Flex>
  );
};

NewMessagePill.propTypes = {
  count: PropTypes.number,
  onClick: PropTypes.func
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function WorkOrderChat({ workOrderId }) {
  // --- State ---
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [websocketStatus, setWebsocketStatus] = useState(webSocketService.isConnected ? 'connected' : 'disconnected');
  const [debugStats, setDebugStats] = useState({
    messagesReceived: 0,
    messagesSent: 0,
    lastError: null,
    // ADDED realtime telemetry
    framesHandled: 0,
    framesIgnored: 0,
    reconnects: 0,
    reconciliations: 0,
    lastFrameAt: null,
    roomSubscribed: false,
    subscribeAttempts: 0,
  });

  // Media States
  const [selectedFile, setSelectedFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [lightboxImage, setLightboxImage] = useState(null);
  const { isOpen: isLightboxOpen, onOpen: openLightbox, onClose: closeLightbox } = useDisclosure();

  // ADDED: realtime UX state
  const [typingUsers, setTypingUsers] = useState({});   // { userId: { name, at } }
  const [unseenCount, setUnseenCount] = useState(0);
  const [isAtBottom, setIsAtBottom] = useState(true);

  // Refs
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  // ADDED refs — these hold values the WebSocket handlers need WITHOUT being
  // listed as effect dependencies. This is what stops the subscription from
  // being torn down and rebuilt on every render (which was silently dropping
  // frames during the teardown window).
  const scrollContainerRef = useRef(null);
  const workOrderIdRef = useRef(workOrderId);
  const isAtBottomRef = useRef(true);
  const lastFrameAtRef = useRef(Date.now());
  const roomSubscribedRef = useRef(false);
  const subscribeRetryRef = useRef(null);
  const subscribeAttemptsRef = useRef(0);
  const heartbeatRef = useRef(null);
  const stalenessRef = useRef(null);
  const fallbackPollRef = useRef(null);
  const typingSweepRef = useRef(null);
  const lastTypingEmitRef = useRef(0);
  const isMountedRef = useRef(true);
  const hasLoadedOnceRef = useRef(false);
  const currentUserRef = useRef(null);

  const toast = useToast();
  const currentUser = getUserInfo();
  currentUserRef.current = currentUser;

  // Keep the ref mirror of workOrderId fresh for handlers.
  useEffect(() => {
    workOrderIdRef.current = workOrderId;
  }, [workOrderId]);

  useEffect(() => {
    isAtBottomRef.current = isAtBottom;
  }, [isAtBottom]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // --- Theme Colors ---
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const ownMessageBg = useColorModeValue('blue.500', 'blue.600');
  const otherMessageBg = useColorModeValue('white', 'gray.700');
  const ownMessageTextColor = useColorModeValue('white', 'whiteAlpha.900');
  const otherMessageTextColor = useColorModeValue('gray.800', 'whiteAlpha.900');
  const ownMessageTimestampColor = useColorModeValue('whiteAlpha.700', 'whiteAlpha.600');
  const otherMessageTimestampColor = useColorModeValue('gray.500', 'gray.400');
  const inputBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const placeholderColor = useColorModeValue('gray.400', 'gray.500');
  const debugPanelBg = useColorModeValue('gray.100', 'gray.800');

  const headerInputBg = useColorModeValue('white', 'gray.800');
  const headerTextColor = useColorModeValue('gray.700', 'white');
  const emptyIconBg = useColorModeValue('gray.100', 'gray.700');
  const emptyTextColor = useColorModeValue('gray.600', 'gray.300');
  const attachmentBg = useColorModeValue('blue.50', 'blue.900');

  // ============================================================================
  // HELPERS
  // ============================================================================

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
    }, 150);
  }, []);

  /**
   * FIXED: the original `updateDebugStats` was called in one place with a
   * FUNCTION argument (`updateDebugStats(prev => ...)`), which spread a
   * function into the state object and corrupted the counters. This version
   * accepts both an object and an updater function.
   */
  const updateDebugStats = useCallback((updates) => {
    setDebugStats(prev => {
      const patch = typeof updates === 'function' ? updates(prev) : updates;
      return { ...prev, ...(patch || {}) };
    });
  }, []);

  /**
   * Determines whether the scroll container is pinned near the bottom. Used to
   * decide between auto-scrolling and showing the "new messages" pill.
   */
  const computeIsAtBottom = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return true;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distance <= REALTIME_CONFIG.autoScrollThresholdPx;
  }, []);

  const handleScroll = useCallback(() => {
    const atBottom = computeIsAtBottom();
    setIsAtBottom(atBottom);
    if (atBottom) setUnseenCount(0);
  }, [computeIsAtBottom]);

  const jumpToLatest = useCallback(() => {
    setUnseenCount(0);
    setIsAtBottom(true);
    scrollToBottom('smooth');
  }, [scrollToBottom]);

  // ============================================================================
  // DATA FETCHING & WEBSOCKET SYNC
  // ============================================================================

  /**
   * loadMessages now takes an options object instead of a bare boolean so the
   * realtime layer can request a silent background reconciliation that does not
   * flash the spinner or clobber optimistic bubbles.
   *
   * Backwards compatible: `loadMessages(true)` still works exactly as before.
   */
  const loadMessages = useCallback(async (options = {}) => {
    const opts = typeof options === 'boolean'
      ? { isInitialLoad: options }
      : options;

    const { isInitialLoad = false, silent = false, reason = 'manual' } = opts;

    const activeWorkOrderId = workOrderIdRef.current;
    if (!activeWorkOrderId) return;

    if (isInitialLoad && !silent) setIsLoading(true);
    if (!silent) setError(null);

    const token = getAuthToken();
    if (!token) {
      if (!silent) setError('Authentication required to load messages.');
      if (isInitialLoad && !silent) setIsLoading(false);
      return;
    }

    try {
      debugLog.info('WorkOrderChat', `Fetching messages (reason: ${reason})`);
      const fetchedMessages = await fetchMessages(activeWorkOrderId, token);

      // Guard against a late response for a work order we already navigated away from.
      if (!isMountedRef.current) return;
      if (!idsMatch(activeWorkOrderId, workOrderIdRef.current)) {
        debugLog.warn('WorkOrderChat', 'Discarding stale fetch for previous work order');
        return;
      }

      setMessages(prev => reconcileServerList(fetchedMessages, prev));

      hasLoadedOnceRef.current = true;
      lastFrameAtRef.current = Date.now();

      updateDebugStats(prev => ({
        messagesReceived: fetchedMessages.length,
        reconciliations: silent ? prev.reconciliations + 1 : prev.reconciliations,
      }));
    } catch (err) {
      debugLog.error('WorkOrderChat', 'Error loading messages', err);
      if (!silent) {
        setError(err.message || 'Failed to load messages.');
      } else {
        // Silent reconciliation failures must not blow away a working UI.
        debugLog.warn('WorkOrderChat', 'Silent reconciliation failed; keeping current view');
      }
      updateDebugStats({ lastError: err.message });
    } finally {
      if (isInitialLoad && !silent) setIsLoading(false);
      if (isInitialLoad && !silent) scrollToBottom('auto');
    }
  }, [scrollToBottom, updateDebugStats]);

  /**
   * Keeps a stable reference to loadMessages so the WebSocket effect never has
   * to depend on it. Without this the effect re-subscribes on every render that
   * changes loadMessages' identity, and frames arriving during the teardown
   * window are lost — one of the reasons realtime appeared to "need a refresh".
   */
  const loadMessagesRef = useRef(loadMessages);
  useEffect(() => {
    loadMessagesRef.current = loadMessages;
  }, [loadMessages]);

  /**
   * Sends the room subscribe frame and schedules retries until the server
   * acknowledges. Idempotent — safe to call repeatedly.
   */
  const subscribeToRoom = useCallback(() => {
    const id = workOrderIdRef.current;
    if (!id) return;
    if (!webSocketService.isConnected) {
      debugLog.websocket('WorkOrderChat', 'Subscribe deferred — socket not connected');
      return;
    }

    subscribeAttemptsRef.current += 1;
    updateDebugStats({ subscribeAttempts: subscribeAttemptsRef.current });

    debugLog.websocket('WorkOrderChat', 'Sending subscribe_work_order', {
      workOrderId: id,
      attempt: subscribeAttemptsRef.current
    });

    // Send both the canonical shape and the normalised-id shape so backends
    // that compare strictly on either type will match the room.
    webSocketService.send({
      type: 'subscribe_work_order',
      workOrderId: id,
      work_order_id: normaliseId(id),
    });

    if (subscribeRetryRef.current) clearTimeout(subscribeRetryRef.current);
    if (subscribeAttemptsRef.current < REALTIME_CONFIG.maxSubscribeRetries) {
      subscribeRetryRef.current = setTimeout(() => {
        if (!roomSubscribedRef.current && webSocketService.isConnected) {
          debugLog.warn('WorkOrderChat', 'No subscribe ack yet — retrying room join');
          subscribeToRoom();
        }
      }, REALTIME_CONFIG.subscribeRetryDelayMs);
    }
  }, [updateDebugStats]);

  const unsubscribeFromRoom = useCallback((id) => {
    if (!id) return;
    if (!webSocketService.isConnected) return;
    debugLog.websocket('WorkOrderChat', 'Sending unsubscribe_work_order', { workOrderId: id });
    webSocketService.send({
      type: 'unsubscribe_work_order',
      workOrderId: id,
      work_order_id: normaliseId(id),
    });
  }, []);

  // --------------------------------------------------------------------------
  // CORE REALTIME EFFECT
  // --------------------------------------------------------------------------
  // Depends ONLY on workOrderId. Everything else is reached through refs, so
  // the socket subscription is established exactly once per work order and
  // stays up for the lifetime of the view. This is the structural fix for
  // "chat doesn't update without a refresh".
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!workOrderId) return undefined;

    debugLog.websocket('WorkOrderChat', 'Mounting realtime layer', { workOrderId });

    roomSubscribedRef.current = false;
    subscribeAttemptsRef.current = 0;

    // Initial history load.
    loadMessagesRef.current({ isInitialLoad: true, reason: 'mount' });

    // ---- Inbound: new message -------------------------------------------
    const handleNewMessage = (data) => {
      lastFrameAtRef.current = Date.now();

      const msg = extractMessagePayload(data);
      if (!msg) {
        debugLog.warn('WorkOrderChat', 'Frame had no extractable message payload', data);
        updateDebugStats(prev => ({ framesIgnored: prev.framesIgnored + 1 }));
        return;
      }

      const frameWoId = readWorkOrderId(msg) ?? readWorkOrderId(data) ?? data?.workOrderId;

      // THE FIX: loose, type-normalised comparison. The original strict `===`
      // failed for number-vs-string and dropped every single frame.
      if (!idsMatch(frameWoId, workOrderIdRef.current)) {
        debugLog.websocket('WorkOrderChat', 'Frame for a different work order — ignored', {
          frameWoId,
          expected: workOrderIdRef.current
        });
        updateDebugStats(prev => ({ framesIgnored: prev.framesIgnored + 1 }));
        return;
      }

      debugLog.info('WorkOrderChat', 'Real-time message received via global WS', msg);

      setMessages(prev => {
        const alreadyPresent = msg.id
          && prev.some(m => normaliseId(m.id) === normaliseId(msg.id));
        const merged = mergeMessage(prev, msg);

        // Decide scroll behaviour based on where the user is reading.
        if (!alreadyPresent) {
          const isOwn = idsMatch(msg.sender_user_id, currentUserRef.current?.id);
          if (isOwn || isAtBottomRef.current) {
            scrollToBottom('smooth');
          } else {
            setUnseenCount(c => c + 1);
          }
        }

        return merged;
      });

      // Clear any typing indicator for the sender — they just sent it.
      const senderId = normaliseId(msg.sender_user_id);
      if (senderId) {
        setTypingUsers(prev => {
          if (!prev[senderId]) return prev;
          const next = { ...prev };
          delete next[senderId];
          return next;
        });
      }

      updateDebugStats(prev => ({
        messagesReceived: prev.messagesReceived + 1,
        framesHandled: prev.framesHandled + 1,
        lastFrameAt: new Date().toISOString(),
      }));
    };

    // ---- Inbound: message edited ----------------------------------------
    const handleMessageUpdated = (data) => {
      lastFrameAtRef.current = Date.now();
      const msg = extractMessagePayload(data);
      if (!msg) return;
      const frameWoId = readWorkOrderId(msg) ?? readWorkOrderId(data);
      if (!idsMatch(frameWoId, workOrderIdRef.current)) return;

      setMessages(prev => prev.map(m =>
        normaliseId(m.id) === normaliseId(msg.id) ? { ...m, ...msg } : m
      ));
      updateDebugStats(prev => ({ framesHandled: prev.framesHandled + 1 }));
    };

    // ---- Inbound: message removed ---------------------------------------
    const handleMessageDeleted = (data) => {
      lastFrameAtRef.current = Date.now();
      const msg = extractMessagePayload(data) || data;
      const targetId = msg?.id ?? msg?.message_id;
      if (!targetId) return;
      const frameWoId = readWorkOrderId(msg) ?? readWorkOrderId(data);
      if (frameWoId && !idsMatch(frameWoId, workOrderIdRef.current)) return;

      setMessages(prev => prev.filter(m => normaliseId(m.id) !== normaliseId(targetId)));
      updateDebugStats(prev => ({ framesHandled: prev.framesHandled + 1 }));
    };

    // ---- Inbound: typing -------------------------------------------------
    const handleTyping = (data) => {
      lastFrameAtRef.current = Date.now();
      const payload = data?.payload || data || {};
      const frameWoId = payload.work_order_id ?? payload.workOrderId;
      if (!idsMatch(frameWoId, workOrderIdRef.current)) return;

      const userId = normaliseId(payload.user_id ?? payload.userId ?? payload.sender_user_id);
      if (!userId) return;
      if (idsMatch(userId, currentUserRef.current?.id)) return; // ignore our own echo

      const name = payload.username || payload.sender_username || 'Someone';
      const isTyping = payload.is_typing ?? payload.isTyping ?? true;

      setTypingUsers(prev => {
        const next = { ...prev };
        if (isTyping) {
          next[userId] = { name, at: Date.now() };
        } else {
          delete next[userId];
        }
        return next;
      });
    };

    // ---- Inbound: read receipt -------------------------------------------
    const handleRead = (data) => {
      lastFrameAtRef.current = Date.now();
      const payload = data?.payload || data || {};
      const frameWoId = payload.work_order_id ?? payload.workOrderId;
      if (!idsMatch(frameWoId, workOrderIdRef.current)) return;

      const readerId = normaliseId(payload.user_id ?? payload.userId);
      if (idsMatch(readerId, currentUserRef.current?.id)) return;

      const readIds = Array.isArray(payload.message_ids)
        ? payload.message_ids.map(normaliseId)
        : payload.message_id ? [normaliseId(payload.message_id)] : [];

      if (readIds.length === 0) return;

      setMessages(prev => prev.map(m =>
        readIds.includes(normaliseId(m.id)) ? { ...m, __read: true } : m
      ));
    };

    // ---- Inbound: subscribe acknowledgement -------------------------------
    const handleSubscribed = (data) => {
      const payload = data?.payload || data || {};
      const frameWoId = payload.work_order_id ?? payload.workOrderId;
      if (frameWoId && !idsMatch(frameWoId, workOrderIdRef.current)) return;

      roomSubscribedRef.current = true;
      if (subscribeRetryRef.current) {
        clearTimeout(subscribeRetryRef.current);
        subscribeRetryRef.current = null;
      }
      debugLog.websocket('WorkOrderChat', 'Room subscription acknowledged by server');
      updateDebugStats({ roomSubscribed: true });
    };

    // ---- Connection status -----------------------------------------------
    const handleStatusUpdate = (event) => {
      const status = event?.status || (webSocketService.isConnected ? 'connected' : 'disconnected');
      debugLog.websocket('WorkOrderChat', `Status changed -> ${status}`);
      setWebsocketStatus(status);

      if (status === 'connected') {
        roomSubscribedRef.current = false;
        subscribeAttemptsRef.current = 0;
        subscribeToRoom();

        // Frames emitted while we were offline are NOT replayed by the Redis
        // pub/sub adapter, so we must reconcile against REST after every
        // reconnect. This closes the gap that previously forced a manual refresh.
        if (REALTIME_CONFIG.reconcileOnReconnect && hasLoadedOnceRef.current) {
          loadMessagesRef.current({ silent: true, reason: 'reconnect-reconcile' });
        }

        updateDebugStats(prev => ({ reconnects: prev.reconnects + 1 }));
      } else {
        roomSubscribedRef.current = false;
        updateDebugStats({ roomSubscribed: false });
      }
    };

    // ---- Register every alias --------------------------------------------
    const registrations = [];

    const bind = (eventNames, handler) => {
      eventNames.forEach(name => {
        webSocketService.subscribe(name, handler);
        registrations.push([name, handler]);
      });
    };

    bind(WS_EVENT_ALIASES.newMessage, handleNewMessage);
    bind(WS_EVENT_ALIASES.messageUpdated, handleMessageUpdated);
    bind(WS_EVENT_ALIASES.messageDeleted, handleMessageDeleted);
    bind(WS_EVENT_ALIASES.typing, handleTyping);
    bind(WS_EVENT_ALIASES.read, handleRead);
    bind(WS_EVENT_ALIASES.subscribed, handleSubscribed);

    webSocketService.subscribe('status', handleStatusUpdate);
    registrations.push(['status', handleStatusUpdate]);

    // If already connected when this component mounts, join the room immediately.
    if (webSocketService.isConnected) {
      subscribeToRoom();
    }

    setWebsocketStatus(webSocketService.isConnected ? 'connected' : 'disconnected');

    // ---- Heartbeat: keeps room membership alive across proxies -------------
    heartbeatRef.current = setInterval(() => {
      if (webSocketService.isConnected) {
        webSocketService.send({
          type: 'ping',
          workOrderId: workOrderIdRef.current,
          work_order_id: normaliseId(workOrderIdRef.current),
        });
        // Re-assert room membership if the server never acked.
        if (!roomSubscribedRef.current) subscribeToRoom();
      }
    }, REALTIME_CONFIG.heartbeatIntervalMs);

    // ---- Staleness watchdog ------------------------------------------------
    // A socket can report "connected" while being functionally dead (NAT
    // timeout, laptop sleep, load balancer idle-kill). If we've heard nothing
    // for too long we silently reconcile so the user never sees a frozen chat.
    stalenessRef.current = setInterval(() => {
      const silentFor = Date.now() - lastFrameAtRef.current;
      if (
        webSocketService.isConnected &&
        silentFor > REALTIME_CONFIG.stalenessThresholdMs &&
        hasLoadedOnceRef.current
      ) {
        debugLog.warn('WorkOrderChat', `No frames for ${Math.round(silentFor / 1000)}s — reconciling`);
        lastFrameAtRef.current = Date.now();
        loadMessagesRef.current({ silent: true, reason: 'staleness-watchdog' });
      }
    }, REALTIME_CONFIG.stalenessThresholdMs);

    // ---- Offline fallback poll ---------------------------------------------
    // Only performs network calls while the socket is DOWN. When the socket is
    // healthy this costs nothing, so realtime remains socket-driven.
    fallbackPollRef.current = setInterval(() => {
      if (!webSocketService.isConnected && hasLoadedOnceRef.current) {
        debugLog.info('WorkOrderChat', 'Socket down — polling fallback tick');
        loadMessagesRef.current({ silent: true, reason: 'offline-fallback-poll' });
      }
    }, REALTIME_CONFIG.fallbackPollIntervalMs);

    // ---- Typing indicator sweeper ------------------------------------------
    typingSweepRef.current = setInterval(() => {
      setTypingUsers(prev => {
        const now = Date.now();
        let changed = false;
        const next = {};
        Object.entries(prev).forEach(([id, entry]) => {
          if (now - entry.at < REALTIME_CONFIG.typingExpiryMs) {
            next[id] = entry;
          } else {
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, 1000);

    // ---- Cleanup -----------------------------------------------------------
    const capturedWorkOrderId = workOrderId;

    return () => {
      debugLog.websocket('WorkOrderChat', 'Tearing down realtime layer', { workOrderId: capturedWorkOrderId });

      registrations.forEach(([name, handler]) => {
        webSocketService.unsubscribe(name, handler);
      });

      unsubscribeFromRoom(capturedWorkOrderId);

      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      if (stalenessRef.current) clearInterval(stalenessRef.current);
      if (fallbackPollRef.current) clearInterval(fallbackPollRef.current);
      if (typingSweepRef.current) clearInterval(typingSweepRef.current);
      if (subscribeRetryRef.current) clearTimeout(subscribeRetryRef.current);

      roomSubscribedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderId]);

  // --------------------------------------------------------------------------
  // TAB VISIBILITY + NETWORK RECOVERY (ADDED)
  // --------------------------------------------------------------------------
  // Browsers throttle or suspend sockets in background tabs. When the user
  // returns we reconcile immediately rather than making them press refresh.
  // --------------------------------------------------------------------------
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && hasLoadedOnceRef.current) {
        debugLog.info('WorkOrderChat', 'Tab became visible — reconciling');
        if (webSocketService.isConnected && !roomSubscribedRef.current) {
          subscribeToRoom();
        }
        loadMessagesRef.current({ silent: true, reason: 'tab-visible' });
      }
    };

    const handleOnline = () => {
      debugLog.info('WorkOrderChat', 'Browser reported online — reconciling');
      if (webSocketService.isConnected) subscribeToRoom();
      if (hasLoadedOnceRef.current) {
        loadMessagesRef.current({ silent: true, reason: 'network-online' });
      }
    };

    const handleFocus = () => {
      if (hasLoadedOnceRef.current && !webSocketService.isConnected) {
        loadMessagesRef.current({ silent: true, reason: 'window-focus' });
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('focus', handleFocus);
    };
  }, [subscribeToRoom]);

  // --------------------------------------------------------------------------
  // OPTIMISTIC TIMEOUT SWEEPER (ADDED)
  // --------------------------------------------------------------------------
  useEffect(() => {
    const timer = setInterval(() => {
      setMessages(prev => {
        let changed = false;
        const next = prev.map(m => {
          if (m.__optimistic && m.__pending && !m.__failed) {
            const age = Date.now() - (m.__createdAt || 0);
            if (age > REALTIME_CONFIG.optimisticTimeoutMs) {
              changed = true;
              return { ...m, __pending: false, __failed: true };
            }
          }
          return m;
        });
        return changed ? next : prev;
      });
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  // --------------------------------------------------------------------------
  // OUTBOUND TYPING NOTIFICATIONS (ADDED)
  // --------------------------------------------------------------------------
  const emitTyping = useCallback((isTyping) => {
    if (!webSocketService.isConnected) return;
    const now = Date.now();
    if (isTyping && now - lastTypingEmitRef.current < REALTIME_CONFIG.typingEmitThrottleMs) {
      return;
    }
    lastTypingEmitRef.current = now;

    webSocketService.send({
      type: 'work_order_typing',
      workOrderId: workOrderIdRef.current,
      work_order_id: normaliseId(workOrderIdRef.current),
      is_typing: isTyping,
      user_id: currentUserRef.current?.id,
      username: currentUserRef.current?.username,
    });
  }, []);

  // --------------------------------------------------------------------------
  // OUTBOUND READ RECEIPTS (ADDED)
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!isAtBottom) return;
    if (!webSocketService.isConnected) return;
    if (messages.length === 0) return;

    const unreadFromOthers = messages
      .filter(m => !m.__optimistic && !idsMatch(m.sender_user_id, currentUserRef.current?.id))
      .filter(m => !m.__readSent)
      .map(m => m.id)
      .filter(Boolean);

    if (unreadFromOthers.length === 0) return;

    webSocketService.send({
      type: 'work_order_message_read',
      workOrderId: workOrderIdRef.current,
      work_order_id: normaliseId(workOrderIdRef.current),
      message_ids: unreadFromOthers,
      user_id: currentUserRef.current?.id,
    });

    setMessages(prev => prev.map(m =>
      unreadFromOthers.some(id => normaliseId(id) === normaliseId(m.id))
        ? { ...m, __readSent: true }
        : m
    ));
  }, [messages, isAtBottom]);

  // --------------------------------------------------------------------------
  // AUTO-SCROLL ON LIST GROWTH (ADDED)
  // --------------------------------------------------------------------------
  const previousCountRef = useRef(0);
  useEffect(() => {
    const grew = messages.length > previousCountRef.current;
    previousCountRef.current = messages.length;
    if (grew && isAtBottomRef.current) {
      scrollToBottom('smooth');
    }
  }, [messages, scrollToBottom]);

  // ============================================================================
  // VOICE RECORDING LOGIC
  // ============================================================================

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const file = new File([audioBlob], `voice_note_${Date.now()}.webm`, { type: 'audio/webm' });
        setSelectedFile(file);

        // Stop all tracks to turn off microphone indicator in browser
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (err) {
      debugLog.error('WorkOrderChat', 'Microphone access denied or failed', err);
      toast({
        title: 'Microphone Error',
        description: 'Please allow microphone access to record voice notes.',
        status: 'error',
        duration: 5000
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setSelectedFile(null);
    setRecordingTime(0);
  };

  const formatRecordingTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Ensure recording resources are released if the component unmounts mid-record.
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      } catch (err) {
        debugLog.warn('WorkOrderChat', 'Failed to stop recorder on unmount', err);
      }
    };
  }, []);

  // ============================================================================
  // FILE ATTACHMENT LOGIC
  // ============================================================================

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 25 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum file size is 25MB', status: 'warning' });
      return;
    }
    setSelectedFile(file);
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ============================================================================
  // SEND MESSAGE LOGIC
  // ============================================================================

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) return;

    setIsSending(true);
    const token = getAuthToken();

    if (!token) {
      toast({ title: 'Authentication Error', status: 'error', duration: 3000 });
      setIsSending(false);
      return;
    }

    const textPayload = newMessage.trim();
    const filePayload = selectedFile;
    const durationPayload = recordingTime;

    // ADDED: optimistic bubble so the sender sees their message instantly,
    // before the server round-trip and before the WS echo returns.
    const clientId = createClientId();
    const optimisticMessage = {
      id: clientId,
      __optimistic: true,
      __pending: true,
      __failed: false,
      __clientId: clientId,
      __createdAt: Date.now(),
      __localFileName: filePayload ? filePayload.name : null,
      client_id: clientId,
      work_order_id: workOrderIdRef.current,
      sender_user_id: currentUserRef.current?.id,
      sender_username: currentUserRef.current?.username,
      message_text: textPayload,
      message_type: filePayload
        ? (filePayload.type.startsWith('image/') ? 'image'
          : filePayload.type.startsWith('audio/') ? 'audio'
            : filePayload.type.startsWith('video/') ? 'video' : 'file')
        : 'text',
      sent_at: new Date().toISOString(),
    };

    setMessages(prev => {
      const next = [...prev, optimisticMessage];
      next.sort(compareMessages);
      return next;
    });
    setIsAtBottom(true);
    setUnseenCount(0);
    scrollToBottom('smooth');

    // Stop broadcasting "typing" the moment the message is sent.
    emitTyping(false);

    // Optimistic Reset
    setNewMessage('');
    clearSelectedFile();
    setRecordingTime(0);

    try {
      let serverMessage = null;

      if (filePayload) {
        // Send Media Message
        serverMessage = await postMediaApi(workOrderId, filePayload, textPayload, durationPayload, token);
        debugLog.info('WorkOrderChat', 'Media message sent successfully');
      } else {
        // Send Text Message
        serverMessage = await postMessageApi(workOrderId, textPayload, token);
        debugLog.info('WorkOrderChat', 'Text message sent successfully');
      }

      // Reconcile the optimistic bubble with the authoritative server row.
      // If the WS echo already arrived and replaced it, mergeMessage is a no-op.
      const resolved = extractMessagePayload(serverMessage) || serverMessage;
      if (resolved && typeof resolved === 'object') {
        const withClientId = { ...resolved, client_id: resolved.client_id || clientId };
        setMessages(prev => mergeMessage(prev, withClientId));
      } else {
        // Server returned no body — just clear the pending flag.
        setMessages(prev => prev.map(m =>
          m.__clientId === clientId ? { ...m, __pending: false } : m
        ));
      }

      updateDebugStats(prev => ({ messagesSent: prev.messagesSent + 1 }));
      scrollToBottom('smooth');
    } catch (err) {
      debugLog.error('WorkOrderChat', 'Failed to send message', err);
      toast({
        title: 'Error Sending Message',
        description: err.message,
        status: 'error',
        duration: 5000
      });

      // Mark the optimistic bubble as failed rather than silently dropping it.
      setMessages(prev => prev.map(m =>
        m.__clientId === clientId
          ? { ...m, __pending: false, __failed: true }
          : m
      ));

      updateDebugStats({ lastError: err.message });

      // Restore on error
      setNewMessage(textPayload);
      if (filePayload) setSelectedFile(filePayload);
    } finally {
      setIsSending(false);
    }
  };

  /**
   * Removes a failed optimistic bubble from the timeline.
   */
  const dismissFailedMessage = useCallback((clientId) => {
    setMessages(prev => prev.filter(m => m.__clientId !== clientId));
  }, []);

  // ============================================================================
  // RENDERING HELPERS
  // ============================================================================

  const renderMedia = (msg) => {
    const mediaUrl = msg.media_url || msg.mediaUrl;

    // ADDED: optimistic media placeholder before the upload completes.
    if (!mediaUrl && msg.__optimistic && msg.__localFileName) {
      return (
        <Flex mt={2} mb={2} align="center" opacity={0.75}>
          <Spinner size="xs" mr={2} />
          <Text fontSize="xs" isTruncated>
            Uploading {msg.__localFileName}…
          </Text>
        </Flex>
      );
    }

    if (!mediaUrl) return null;

    const fullUrl = getMediaUrl(mediaUrl);
    const messageType = (msg.message_type || msg.messageType || 'file').toLowerCase();

    switch (messageType) {
      case 'image':
        return (
          <Box mt={2} mb={2} borderRadius="md" overflow="hidden" position="relative" role="group">
            <Image
              src={fullUrl}
              alt="Attachment"
              maxH="250px"
              w="100%"
              objectFit="cover"
              fallback={<Skeleton height="200px" width="100%" borderRadius="md" />}
              cursor="pointer"
              onClick={() => { setLightboxImage(fullUrl); openLightbox(); }}
            />
            <Flex
              position="absolute"
              top={0} left={0} right={0} bottom={0}
              bg="blackAlpha.400"
              opacity={0}
              _groupHover={{ opacity: 1 }}
              transition="opacity 0.2s"
              align="center"
              justify="center"
              cursor="pointer"
              onClick={() => { setLightboxImage(fullUrl); openLightbox(); }}
            >
              <Icon as={MdZoomIn} color="white" boxSize={8} />
            </Flex>
          </Box>
        );
      case 'audio':
      case 'voice':
        return (
          <Box mt={2} mb={2} w="100%" minW="200px">
            <audio controls src={fullUrl} style={{ width: '100%', height: '36px', outline: 'none' }}>
              Your browser does not support the audio element.
            </audio>
          </Box>
        );
      case 'video':
        return (
          <Box mt={2} mb={2} borderRadius="md" overflow="hidden">
            <video controls src={fullUrl} style={{ maxWidth: '100%', maxHeight: '250px' }}>
              Your browser does not support the video tag.
            </video>
          </Box>
        );
      case 'file':
      default:
        return (
          <Button
            as="a"
            href={fullUrl}
            target="_blank"
            size="sm"
            mt={2}
            mb={2}
            leftIcon={<Icon as={MdDownload} />}
            colorScheme={idsMatch(msg.sender_user_id, currentUser?.id) ? "blue" : "gray"}
            variant={idsMatch(msg.sender_user_id, currentUser?.id) ? "solid" : "outline"}
            w="full"
          >
            Download Attachment
          </Button>
        );
    }
  };

  const groupedMessages = useMemo(() => groupMessagesByDate(messages), [messages]);

  const typingNames = useMemo(
    () => Object.values(typingUsers).map(t => t.name),
    [typingUsers]
  );

  const getWebSocketStatusIndicator = () => {
    const indicators = {
      connected: { icon: MdCloudDone, color: 'green', label: 'Real-time' },
      connecting: { icon: MdCloudQueue, color: 'yellow', label: 'Connecting...' },
      disconnected: { icon: MdCloudOff, color: 'gray', label: 'Offline' },
      error: { icon: MdErrorOutline, color: 'red', label: 'Error' }
    };
    const { icon, color, label } = indicators[websocketStatus] || indicators.disconnected;
    return (
      <Tooltip label={`Connection Status: ${label}`}>
        <Tag size="sm" colorScheme={color} variant="subtle" borderRadius="full">
          <Icon as={icon} mr={1} />
          {label}
        </Tag>
      </Tooltip>
    );
  };

  /**
   * Resolves the delivery glyph state for one of the current user's messages.
   */
  const resolveDeliveryState = (msg) => {
    if (msg.__failed) return 'failed';
    if (msg.__pending) return 'pending';
    if (msg.__read) return 'read';
    return 'sent';
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <Flex
      direction="column"
      h="100%"
      maxH={{ base: 'calc(100vh - 200px)', md: '650px' }}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      bg={bgColor}
      boxShadow="sm"
      position="relative"
    >
      {/* --- Lightbox Portal --- */}
      <Portal>
        <ImageLightbox isOpen={isLightboxOpen} onClose={closeLightbox} src={lightboxImage} />
      </Portal>

      {/* --- Header --- */}
      <Flex
        justify="space-between"
        align="center"
        p={3}
        borderBottomWidth="1px"
        borderColor={borderColor}
        bg={headerInputBg}
        borderTopRadius="xl"
      >
        <Text fontWeight="bold" fontSize="md" color={headerTextColor}>
          Work Order Chat
        </Text>
        <HStack spacing={2}>
          {/* ADDED: subtle live badge so users can tell realtime is actually on */}
          {websocketStatus === 'connected' && (
            <Badge colorScheme="green" variant="subtle" borderRadius="full" px={2} fontSize="10px">
              LIVE
            </Badge>
          )}
          {getWebSocketStatusIndicator()}
          {DEBUG_CONFIG.enabled && (
            <Tooltip label="Toggle debug panel">
              <IconButton
                icon={<Icon as={MdBugReport} />}
                size="xs"
                variant="ghost"
                onClick={() => setShowDebugPanel(!showDebugPanel)}
                colorScheme={showDebugPanel ? 'blue' : 'gray'}
                aria-label="Debug"
              />
            </Tooltip>
          )}
          <Tooltip label="Refresh timeline">
            <IconButton
              icon={<Icon as={MdRefresh} />}
              size="xs"
              variant="ghost"
              onClick={() => loadMessages({ isInitialLoad: true, reason: 'manual-refresh' })}
              isLoading={isLoading}
              aria-label="Refresh"
            />
          </Tooltip>
        </HStack>
      </Flex>

      {/* --- Debug Panel --- */}
      {DEBUG_CONFIG.enabled && showDebugPanel && (
        <Box p={3} bg={debugPanelBg} borderBottomWidth="1px" borderColor={borderColor} fontSize="xs" fontFamily="mono">
          <HStack justify="space-between" mb={1}>
            <Text fontWeight="bold" color="blue.500">WS Context: {websocketStatus.toUpperCase()}</Text>
            <IconButton icon={<Icon as={MdClose} />} size="xs" variant="ghost" onClick={() => setShowDebugPanel(false)} aria-label="Close" />
          </HStack>
          <Stack spacing={0.5}>
            <Text>Message Count: {messages.length}</Text>
            <Text>Current User: {currentUser?.username || 'Unknown'}</Text>
            <Text>Work Order ID: {String(workOrderId)} ({typeof workOrderId})</Text>
            <Text>Room Subscribed: {debugStats.roomSubscribed ? 'YES' : 'NO'} (attempts: {debugStats.subscribeAttempts})</Text>
            <Text>Frames Handled: {debugStats.framesHandled} / Ignored: {debugStats.framesIgnored}</Text>
            <Text>Reconnects: {debugStats.reconnects} / Reconciliations: {debugStats.reconciliations}</Text>
            <Text>Messages Sent: {debugStats.messagesSent}</Text>
            <Text>Last Frame: {debugStats.lastFrameAt || 'never'}</Text>
            <Text>Pinned To Bottom: {isAtBottom ? 'YES' : 'NO'}</Text>
            <Text>Typing: {typingNames.length > 0 ? typingNames.join(', ') : 'none'}</Text>
            {(error || debugStats.lastError) && (
              <Text color="red.500">Last Error: {error || debugStats.lastError}</Text>
            )}
          </Stack>
        </Box>
      )}

      {/* --- Messages Area --- */}
      <Box
        flex="1"
        overflowY="auto"
        p={4}
        ref={scrollContainerRef}
        onScroll={handleScroll}
        css={{ '&::-webkit-scrollbar': { width: '8px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '4px' } }}
      >
        {isLoading ? (
          <Flex justify="center" align="center" h="100%">
            <VStack spacing={3}>
              <Spinner size="xl" color="blue.500" thickness="4px" speed="0.65s" />
              <Text color={placeholderColor} fontSize="sm" fontWeight="500">Syncing timeline...</Text>
            </VStack>
          </Flex>
        ) : error ? (
          <Alert status="error" borderRadius="md" variant="left-accent">
            <AlertIcon />
            <Box flex="1">
              <AlertTitle fontSize="sm">Connection Error</AlertTitle>
              <AlertDescription fontSize="xs">{error}</AlertDescription>
            </Box>
            <Button size="xs" colorScheme="red" onClick={() => loadMessages({ isInitialLoad: true, reason: 'error-retry' })}>Retry</Button>
          </Alert>
        ) : messages.length === 0 ? (
          <Flex direction="column" align="center" justify="center" h="100%" textAlign="center" p={6}>
            <Box bg={emptyIconBg} p={4} borderRadius="full" mb={4}>
              <Icon as={MdSend} boxSize="2rem" color={placeholderColor} />
            </Box>
            <Text fontSize="md" fontWeight="bold" color={emptyTextColor}>
              No messages yet
            </Text>
            <Text fontSize="sm" color={placeholderColor} mt={1}>
              Be the first to share an update, photo, or voice note.
            </Text>
          </Flex>
        ) : (
          <VStack spacing={4} align="stretch">
            {Object.entries(groupedMessages).map(([date, dateMessages]) => (
              <Box key={date}>
                {/* Date Separator */}
                <Flex align="center" my={4}>
                  <Divider borderColor={borderColor} />
                  <Text px={3} fontSize="xs" fontWeight="bold" color={placeholderColor} textTransform="uppercase" whiteSpace="nowrap">
                    {date}
                  </Text>
                  <Divider borderColor={borderColor} />
                </Flex>

                {/* Message Bubbles */}
                <VStack spacing={3} align="stretch">
                  {dateMessages.map((msg) => {
                    const msgId = msg.id?.toString() || `msg-${msg.sent_at || Date.now()}`;
                    // FIXED: loose id comparison so own-message alignment works
                    // when the API returns numeric ids and auth returns strings.
                    const isOwnMessage = idsMatch(msg.sender_user_id, currentUser?.id);
                    const deliveryState = resolveDeliveryState(msg);

                    return (
                      <Flex key={msgId} w="100%" justify={isOwnMessage ? 'flex-end' : 'flex-start'}>
                        <Box
                          bg={isOwnMessage ? ownMessageBg : otherMessageBg}
                          px={4}
                          py={3}
                          borderRadius="2xl"
                          borderBottomRightRadius={isOwnMessage ? "sm" : "2xl"}
                          borderBottomLeftRadius={!isOwnMessage ? "sm" : "2xl"}
                          maxW={{ base: '90%', md: '75%' }}
                          boxShadow="sm"
                          borderWidth={isOwnMessage ? 0 : '1px'}
                          borderColor={borderColor}
                          opacity={msg.__pending ? 0.7 : 1}
                          transition="opacity 0.2s ease"
                        >
                          {/* Sender Name */}
                          {!isOwnMessage && (
                            <Text fontWeight="bold" fontSize="xs" mb={1} color={otherMessageTimestampColor}>
                              {msg.sender_username || 'System'}
                            </Text>
                          )}

                          {/* Media Attachment */}
                          {(msg.media_url || msg.__localFileName) && renderMedia(msg)}

                          {/* Text Content */}
                          {msg.message_text && (
                            <Text
                              fontSize="sm"
                              color={isOwnMessage ? ownMessageTextColor : otherMessageTextColor}
                              whiteSpace="pre-wrap"
                              lineHeight="tall"
                            >
                              {msg.message_text}
                            </Text>
                          )}

                          {/* Failed-send retry affordance */}
                          {msg.__failed && (
                            <Flex align="center" mt={2}>
                              <Text fontSize="10px" color="red.200" mr={2}>
                                Not delivered
                              </Text>
                              <Button
                                size="xs"
                                variant="link"
                                colorScheme="red"
                                onClick={() => dismissFailedMessage(msg.__clientId)}
                              >
                                Dismiss
                              </Button>
                            </Flex>
                          )}

                          {/* Timestamp + delivery state */}
                          <Flex
                            justify={isOwnMessage ? 'flex-end' : 'flex-start'}
                            align="center"
                            mt={1.5}
                          >
                            <Text
                              fontSize="10px"
                              color={isOwnMessage ? ownMessageTimestampColor : otherMessageTimestampColor}
                              fontWeight="500"
                            >
                              {msg.__pending ? 'Sending…' : formatTimeAgo(msg.sent_at)}
                            </Text>
                            {isOwnMessage && (
                              <DeliveryState
                                state={deliveryState}
                                color={ownMessageTimestampColor}
                              />
                            )}
                          </Flex>
                        </Box>
                      </Flex>
                    );
                  })}
                </VStack>
              </Box>
            ))}

            {/* ADDED: live typing indicator */}
            <TypingIndicator
              names={typingNames}
              bg={otherMessageBg}
              borderColor={borderColor}
              textColor={otherMessageTimestampColor}
            />

            <div ref={messagesEndRef} />
          </VStack>
        )}
      </Box>

      {/* ADDED: jump-to-latest pill for when new messages land while scrolled up */}
      <NewMessagePill count={unseenCount} onClick={jumpToLatest} />

      {/* --- Input Area --- */}
      <Box p={3} borderTopWidth="1px" borderColor={borderColor} bg={headerInputBg} borderBottomRadius="xl">

        {/* Attachment Preview Overlay */}
        {selectedFile && !isRecording && (
          <Flex align="center" bg={attachmentBg} p={2} borderRadius="md" mb={3} borderWidth="1px" borderColor="blue.200">
            <Icon as={selectedFile.type.startsWith('audio/') ? MdMic : MdImage} color="blue.500" mr={2} boxSize={5} />
            <Text fontSize="sm" flex="1" isTruncated fontWeight="500">
              {selectedFile.name}
            </Text>
            <IconButton icon={<Icon as={MdClose} />} size="xs" variant="ghost" colorScheme="red" onClick={clearSelectedFile} aria-label="Remove attachment" />
          </Flex>
        )}

        {/* Voice Recording Overlay */}
        {isRecording && (
          <Flex align="center" bg="red.50" p={2} borderRadius="md" mb={3} borderWidth="1px" borderColor="red.200">
            <Box w="8px" h="8px" borderRadius="full" bg="red.500" className="recording-dot" mr={3} />
            <Text fontSize="sm" flex="1" fontWeight="bold" color="red.600">
              Recording Voice Note: {formatRecordingTime(recordingTime)}
            </Text>
            <HStack spacing={2}>
              <Button size="xs" colorScheme="red" onClick={stopRecording}>Stop & Attach</Button>
              <Button size="xs" variant="ghost" colorScheme="gray" onClick={cancelRecording}>Cancel</Button>
            </HStack>
          </Flex>
        )}

        {/* Main Input Row */}
        <HStack spacing={2} align="flex-end">
          {/* File Upload Hidden Input */}
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx" />

          <Tooltip label="Attach File">
            <IconButton
              icon={<Icon as={MdAttachFile} boxSize={5} />}
              aria-label="Attach File"
              variant="ghost"
              colorScheme="gray"
              onClick={() => fileInputRef.current?.click()}
              isDisabled={isSending || isLoading || isRecording}
              size="md"
              borderRadius="full"
            />
          </Tooltip>

          {/* Voice Note Button */}
          <Tooltip label={isRecording ? "Stop Recording" : "Record Voice Note"}>
            <IconButton
              icon={<Icon as={isRecording ? MdStop : MdMic} boxSize={5} />}
              aria-label="Voice Note"
              variant={isRecording ? "solid" : "ghost"}
              colorScheme={isRecording ? "red" : "gray"}
              onClick={isRecording ? stopRecording : startRecording}
              isDisabled={isSending || isLoading || !!selectedFile}
              size="md"
              borderRadius="full"
            />
          </Tooltip>

          <Textarea
            placeholder={selectedFile ? "Add an optional caption..." : "Type your message..."}
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              // ADDED: broadcast typing state (throttled internally)
              emitTyping(e.target.value.length > 0);
            }}
            onBlur={() => emitTyping(false)}
            minRows={1}
            maxRows={4}
            size="sm"
            bg={inputBg}
            isDisabled={isSending || isLoading || isRecording}
            borderRadius="xl"
            focusBorderColor="blue.500"
            borderColor={borderColor}
            resize="none"
            py={3}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if ((newMessage.trim() || selectedFile) && !isSending) {
                  handleSendMessage();
                }
              }
            }}
          />

          <Tooltip label="Send Message">
            <IconButton
              icon={<Icon as={MdSend} boxSize={5} />}
              aria-label="Send Message"
              colorScheme="blue"
              onClick={handleSendMessage}
              isLoading={isSending}
              isDisabled={(!newMessage.trim() && !selectedFile) || isLoading || isRecording}
              size="md"
              h="40px"
              w="40px"
              borderRadius="full"
            />
          </Tooltip>
        </HStack>
      </Box>

      {/* Embedded CSS for pulsing recording dot + typing dots */}
      <style>{`
        @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.3; } 100% { opacity: 1; } }
        .recording-dot { animation: pulse 1s infinite ease-in-out; }

        @keyframes typingBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        .typing-dot { animation: typingBounce 1.2s infinite ease-in-out; }
        .typing-dot-1 { animation-delay: 0s; }
        .typing-dot-2 { animation-delay: 0.15s; }
        .typing-dot-3 { animation-delay: 0.3s; }
      `}</style>
    </Flex>
  );
}

// ============================================================================
// PROP TYPES
// ============================================================================

WorkOrderChat.propTypes = {
  // RELAXED: the parent frequently passes a numeric id from the API. Declaring
  // string-only produced console warnings and masked the type mismatch that was
  // breaking realtime frame matching.
  workOrderId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired
};

export default WorkOrderChat;